/**
 * "I sent a transfer" (owner, 2026-10-31): the client tells us the amount, the day and the
 * registered account a transfer left from, for one of their series; the Account Executive of
 * their roster can record the same notice for them. It moves no money: it helps us match the
 * transfer when it arrives, and our team sees it next to any transfer we could not identify.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import bankAccountsService, { type ClientBankAccount } from '../../services/bankAccounts.service';
import depositNoticesService, { type DepositNotice } from '../../services/depositNotices.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatCents, limitToCents } from '../../lib/amount';
import { calendarDate } from './enteringFund.helpers';
import { colors } from '../../styles/theme';
import {
  EMPTY_NOTICE,
  NOTICE_STATUS_LABEL,
  accountLabel,
  accountsFor,
  localDay,
  noticeProblem,
  type NoticeDraft,
  type NoticeSeries,
} from './depositNotice.helpers';

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };

interface Props {
  clientId: string;
  series: readonly NoticeSeries[];
  /** `agent`: the Account Executive records it for the client, in their words. */
  audience: 'client' | 'agent';
}

export default function DepositNoticeCard({ clientId, series, audience }: Readonly<Props>): JSX.Element | null {
  const [accounts, setAccounts] = useState<ClientBankAccount[]>([]);
  const [notices, setNotices] = useState<DepositNotice[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NoticeDraft>(EMPTY_NOTICE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [acc, list] = await Promise.all([
        bankAccountsService.listForClient(clientId),
        depositNoticesService.list(clientId),
      ]);
      setAccounts(acc);
      setNotices(list);
    } catch (e: unknown) {
      setLoadError(getApiErrorMessage(e, 'We could not load your transfer notices. Reload the page to try again.'));
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const chosen = useMemo(() => series.find((s) => s.product_id === draft.productId), [series, draft.productId]);
  const usable = accountsFor(accounts, chosen);
  const today = localDay(new Date());
  const problem = noticeProblem(draft, today);

  if (series.length === 0) return null;

  const submit = async (): Promise<void> => {
    if (!chosen?.currency_code) return;
    setBusy(true);
    setError(null);
    try {
      await depositNoticesService.record(clientId, {
        product_id: draft.productId,
        client_bank_account_id: draft.accountId,
        amount: draft.amount,
        currency_code: chosen.currency_code,
        sent_on: draft.sentOn,
      });
      setOpen(false);
      setDraft(EMPTY_NOTICE);
      setFlash(
        audience === 'client'
          ? "Thanks. We'll look for your transfer and confirm it when it arrives."
          : 'Notice recorded. The transfer is matched when it arrives from that account.',
      );
      await load();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'We could not save your notice. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8" aria-labelledby={`notice-${clientId}`}>
      <h2 id={`notice-${clientId}`} className="text-sm font-bold uppercase tracking-widest mb-3"
          style={{ color: colors.inkSoft }}>
        {audience === 'client' ? 'Already sent a transfer?' : 'Transfer notice'}
      </h2>
      <div className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <p className="text-sm mb-3" style={{ color: colors.muted }}>
          {audience === 'client'
            ? "Tell us and we'll look for it, so it is confirmed sooner."
            : 'Record what the client told you about a transfer they sent. Nothing is credited by it.'}
        </p>
        {flash && (
          <output className="block text-sm mb-3" style={{ color: colors.success }}>{flash}</output>
        )}
        {loadError && (
          <p className="text-sm mb-3" role="alert" style={{ color: colors.danger }}>{loadError}</p>
        )}
        {!open && (
          <button type="button" onClick={() => { setOpen(true); setFlash(null); }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            {audience === 'client' ? 'Tell us about your transfer' : 'Record a transfer notice'}
          </button>
        )}
        {open && (
          <div className="grid gap-3">
            {error && (
              <p className="text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>
            )}
            <label className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Series{' '}
              <select value={draft.productId} disabled={busy}
                      onChange={(e) => setDraft({ ...draft, productId: e.target.value, accountId: '' })}
                      className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="">Choose</option>
                {series.map((s) => (
                  <option key={s.product_id} value={s.product_id}>{s.product_name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Account it was sent from{' '}
              <select value={draft.accountId} disabled={busy || !chosen}
                      onChange={(e) => setDraft({ ...draft, accountId: e.target.value })}
                      className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="">Choose</option>
                {usable.map((a) => (
                  <option key={a.id} value={a.id}>{accountLabel(a)}</option>
                ))}
              </select>
            </label>
            {chosen && usable.length === 0 && (
              <p className="text-xs" style={{ color: colors.warning }}>
                Add the account you send from first, in the same currency as the series.
              </p>
            )}
            <label className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Amount{chosen?.currency_code ? ` (${chosen.currency_code})` : ''}
              <input value={draft.amount} disabled={busy} inputMode="decimal"
                     onChange={(e) => setDraft({ ...draft, amount: limitToCents(e.target.value) })}
                     className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </label>
            <label className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
              Day it was sent{' '}
              <input type="date" value={draft.sentOn} max={today} disabled={busy}
                     onChange={(e) => setDraft({ ...draft, sentOn: e.target.value })}
                     className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </label>
            {problem && <p className="text-xs" style={{ color: colors.mutedSoft }}>{problem}</p>}
            <div className="flex gap-2">
              <button type="button" disabled={problem !== null || busy} onClick={() => { void submit(); }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      style={{ background: colors.navy, color: '#FFFFFF' }}>
                {busy ? 'Sending…' : 'Send notice'}
              </button>
              <button type="button" disabled={busy}
                      onClick={() => { setOpen(false); setDraft(EMPTY_NOTICE); setError(null); }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {notices.length > 0 && (
          <ul className="mt-4 space-y-2">
            {notices.map((n) => (
              <li key={n.id} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div style={{ color: colors.ink }}>{n.product_name}</div>
                  <div className="text-xs" style={{ color: colors.mutedSoft }}>
                    {calendarDate(n.sent_on)}
                    {n.recorded_by_client ? '' : ' · '}
                    {n.recorded_by_client ? null : <span>Recorded by your Account Executive</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono" style={{ color: colors.inkSoft }}>
                    {n.currency_code} {formatCents(n.amount)}
                  </div>
                  <div className="text-xs" style={{ color: colors.muted }}>{NOTICE_STATUS_LABEL[n.status]}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
